import type { ReactNode } from 'react';
import { BarChart3, Mail, Webhook, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import MermaidDiagram from './MermaidDiagram';
import { TRACKING_DATA_FLOW_CHART, TRACKING_OVERVIEW_CHART } from './emailTrackingCharts';

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{title}</h2>
      <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

const toc = [
  { id: 'overview', label: 'Overview' },
  { id: 'on-send', label: 'What we enable on send' },
  { id: 'webhook', label: 'Postmark webhook' },
  { id: 'storage', label: 'Database tables' },
  { id: 'rollup', label: 'Daily rollup' },
  { id: 'ui', label: 'Where you see it' },
];

export default function EmailTrackingGuideView() {
  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-sky-50 dark:bg-sky-900/30 border border-sky-200 dark:border-sky-800">
            <BarChart3 className="w-6 h-6 text-sky-600 dark:text-sky-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Email open &amp; click tracking
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              How Postmark events flow back into the database and the admin UI.
            </p>
          </div>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Related:{' '}
          <Link
            to="/guide/email-pipeline"
            className="text-emerald-600 dark:text-emerald-400 underline font-medium"
          >
            Email send pipeline
          </Link>
          {' · '}
          <Link
            to="/guide/inngest"
            className="text-violet-600 dark:text-violet-400 underline font-medium"
          >
            Inngest jobs
          </Link>
          {' · '}
          <Link
            to="/guide/edge-functions"
            className="text-orange-600 dark:text-orange-400 underline font-medium"
          >
            Edge functions
          </Link>
          . Tracking starts <strong>after</strong> a row reaches{' '}
          <code className="text-xs font-mono">sent</code>.
        </p>
      </div>

      <nav className="mb-8 flex flex-wrap gap-2 text-xs">
        {toc.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            className="px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-sky-50 dark:hover:bg-sky-900/20 hover:border-sky-300 dark:hover:border-sky-700 transition-colors"
          >
            {item.label}
          </a>
        ))}
      </nav>

      <div className="space-y-10">
        <Section id="overview" title="Overview">
          <p>
            We do not track opens/clicks ourselves in the browser. <strong>Postmark</strong> records
            them when the message is sent with tracking enabled, then POSTs webhooks to our Supabase
            edge function. Each open or click becomes one row in{' '}
            <code className="text-xs font-mono">email_engagement_events</code>, linked to a send via
            Postmark’s <code className="text-xs font-mono">MessageID</code>.
          </p>
          <MermaidDiagram
            chart={TRACKING_OVERVIEW_CHART}
            caption="Feedback loop is separate from the send cron — it runs when recipients interact with mail."
          />
          <MermaidDiagram
            chart={TRACKING_DATA_FLOW_CHART}
            caption="Sequence view of the same path."
          />
        </Section>

        <Section id="on-send" title="What we enable on send">
          <p>
            Every scheduled email sent through{' '}
            <code className="text-xs font-mono">processScheduledEmailRow</code> uses Postmark with:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>TrackOpens: true</strong> — invisible pixel / equivalent for open detection
            </li>
            <li>
              <strong>TrackLinks: HtmlAndText</strong> — links in HTML and text parts are rewritten
              for click tracking
            </li>
            <li>
              <strong>Tag:</strong> <code className="font-mono text-xs">scheduled-email</code>
            </li>
            <li>
              Optional <strong>Metadata</strong> on the message (newsletter_id, automation_step_id) for
              Postmark’s dashboard — webhook matching uses{' '}
              <code className="font-mono text-xs">MessageID</code>, not metadata
            </li>
          </ul>
          <p>
            On success, the row stores <code className="text-xs font-mono">postmark_message_id</code>{' '}
            (and <code className="text-xs font-mono">provider</code> like{' '}
            <code className="font-mono text-xs">postmark:…</code>). That ID is how webhooks find the
            right <code className="text-xs font-mono">scheduled_emails</code> row.
          </p>
          <p className="text-xs text-gray-500">
            Raw HTML sends without a Postmark template still go through Postmark’s API with the same
            tracking flags.
          </p>
        </Section>

        <Section id="webhook" title="Postmark webhook">
          <div className="flex items-start gap-2 rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800/40">
            <Webhook className="w-5 h-5 text-sky-500 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-gray-900 dark:text-white">Edge function: postmark-webhook</p>
              <p className="mt-1 text-gray-600 dark:text-gray-400">
                POST only. URL includes a secret segment:{' '}
                <code className="font-mono text-xs">…/functions/v1/postmark-webhook/&lt;POSTMARK_WEBHOOK_SECRET&gt;</code>
              </p>
            </div>
          </div>
          <p>For each payload:</p>
          <ol className="list-decimal pl-5 space-y-2">
            <li>
              Read <code className="text-xs font-mono">RecordType</code> — only{' '}
              <strong>Open</strong> and <strong>Click</strong> are stored; others return{' '}
              <code className="text-xs font-mono">ok, skipped</code>
            </li>
            <li>
              Read <code className="text-xs font-mono">MessageID</code> and look up{' '}
              <code className="text-xs font-mono">scheduled_emails</code> by{' '}
              <code className="text-xs font-mono">postmark_message_id</code> (fallback:{' '}
              <code className="text-xs font-mono">provider</code>)
            </li>
            <li>
              Insert into <code className="text-xs font-mono">email_engagement_events</code>:{' '}
              <code className="text-xs font-mono">event_type</code> open or click,{' '}
              <code className="text-xs font-mono">occurred_at</code> from Postmark{' '}
              <code className="text-xs font-mono">ReceivedAt</code>,{' '}
              <code className="text-xs font-mono">destination_url</code> for clicks (
              <code className="text-xs font-mono">OriginalLink</code>)
            </li>
          </ol>
          <p>
            If MessageID does not match any send (misconfigured webhook, old message, test mail), the
            event is still inserted but <code className="text-xs font-mono">scheduled_email_id</code>{' '}
            is null — it will not appear on per-recipient UI badges.
          </p>
        </Section>

        <Section id="storage" title="Database tables">
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 not-prose">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2">Table</th>
                  <th className="px-3 py-2">Role</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">email_engagement_events</td>
                  <td className="px-3 py-2">
                    Append-only stream: one row per open or click. Used for live stats while the
                    campaign is “active.”
                  </td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">email_engagement_rollups</td>
                  <td className="px-3 py-2">
                    Aggregated metrics per newsletter or automation step (and per link URL for click
                    breakdown). Used after rollup; raw events for that campaign are deleted.
                  </td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">scheduled_emails</td>
                  <td className="px-3 py-2">
                    Must be <code className="font-mono text-xs">sent</code> with{' '}
                    <code className="font-mono text-xs">postmark_message_id</code> set for linkage.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            <strong>Live vs rollup in the UI:</strong> the app queries raw events first (
            <code className="text-xs font-mono">source: live</code>). If none remain, it reads{' '}
            <code className="text-xs font-mono">email_engagement_rollups</code> (
            <code className="text-xs font-mono">source: rollup</code>).
          </p>
        </Section>

        <Section id="rollup" title="Daily rollup (measurement window)">
          <p>
            Inngest function <code className="text-xs font-mono">rollup-email-engagement</code> runs{' '}
            <strong>daily at 06:00 UTC</strong> (and on manual event{' '}
            <code className="text-xs font-mono">email-engagement/rollup.requested</code>).
          </p>
          <p>For each newsletter or automation step that is “ready”:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Last send for that campaign was at least{' '}
              <strong>N days ago</strong> (default <strong>14</strong>, env{' '}
              <code className="font-mono text-xs">EMAIL_ENGAGEMENT_ROLLUP_WINDOW_DAYS</code>)
            </li>
            <li>Not already rolled up for that window</li>
          </ul>
          <p>Then it:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Counts unique opens and clickers from raw events</li>
            <li>Writes summary rows to <code className="text-xs font-mono">email_engagement_rollups</code> (plus per-URL click rows)</li>
            <li>
              <strong>Deletes</strong> raw <code className="text-xs font-mono">email_engagement_events</code>{' '}
              for sends in that campaign — keeps the events table small
            </li>
          </ol>
          <p className="text-xs text-gray-500">
            Until rollup runs, opens/clicks keep accumulating in the live table. After rollup, the
            newsletter Tracking tab still shows totals from rollups.
          </p>
        </Section>

        <Section id="ui" title="Where you see it in the app">
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Newsletter → Tracking tab</strong> — campaign-level opens, clickers, link
              breakdown; polls while sends are active
            </li>
            <li>
              <strong>Newsletter → Recipients</strong> — open/click icons per row when{' '}
              <code className="text-xs font-mono">scheduled_email_id</code> is linked
            </li>
            <li>
              <strong>Subscriber → Engagement tab</strong> — event list for that person’s sends
            </li>
            <li>
              <strong>Automation step → Engagement</strong> — same metrics scoped to a step
            </li>
          </ul>
        </Section>

        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
          <Mail className="w-4 h-4 shrink-0" />
          <span>
            Configure Postmark’s webhook URL to point at your project’s{' '}
            <code className="font-mono text-xs">postmark-webhook</code> function with the matching
            secret. Without that, sends still work but opens/clicks never arrive.
          </span>
          <ArrowRight className="w-4 h-4 shrink-0 opacity-50" />
        </div>
      </div>
    </div>
  );
}
